import { GlobalWorkerOptions, getDocument } from 'pdfjs-dist/legacy/build/pdf.mjs';
import { searchPufCustomers, searchPufItems } from './pricingUpdateLookup';
import {
  createPricingUpdateItemRow,
  type PricingUpdateFormData,
  type PricingUpdateItemRow,
} from '../types/applicationForm';

GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/legacy/build/pdf.worker.min.mjs',
  import.meta.url,
).toString();

type PdfTextItem = { str: string; x: number; y: number };

export type PrfParsedLine = {
  sn: string;
  item_no: string;
  current: number | null;
  market: number | null;
  propose: number | null;
  approved: number | null;
  uom: string;
  moq: string;
};

export type PrfImportSkip = {
  item_no: string;
  reason: string;
};

export type PrfImportResult = {
  referenceNo: string;
  customerNo: string;
  customerName: string;
  customerPriceGroup: string;
  imported: PricingUpdateItemRow[];
  skipped: PrfImportSkip[];
  formPatch: Partial<PricingUpdateFormData>;
};

const PDF_TEXT_TIMEOUT_MS = 20_000;

async function withTimeout<T>(promise: Promise<T>, ms: number, label: string): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([
      promise,
      new Promise<T>((_, reject) => {
        timer = setTimeout(() => {
          reject(new Error(`${label} timed out after ${Math.round(ms / 1000)}s.`));
        }, ms);
      }),
    ]);
  } finally {
    if (timer !== undefined) clearTimeout(timer);
  }
}

async function extractPdfTextItems(bytes: Uint8Array): Promise<PdfTextItem[]> {
  const doc = await getDocument({ data: bytes }).promise;
  const items: PdfTextItem[] = [];
  const pageLimit = Math.min(doc.numPages, 6);
  for (let pageNum = 1; pageNum <= pageLimit; pageNum += 1) {
    const page = await doc.getPage(pageNum);
    const textContent = await page.getTextContent();
    const pageYOffset = (pageLimit - pageNum) * 2000;
    for (const item of textContent.items) {
      if (!('str' in item)) continue;
      const str = String(item.str ?? '');
      if (!str.trim()) continue;
      const transform = 'transform' in item && Array.isArray(item.transform) ? item.transform : null;
      const x = typeof transform?.[4] === 'number' ? transform[4] : 0;
      const y = typeof transform?.[5] === 'number' ? transform[5] + pageYOffset : pageYOffset;
      items.push({ str, x, y });
    }
  }
  return items;
}

function groupRows(items: PdfTextItem[], yTolerance = 4): PdfTextItem[][] {
  if (items.length === 0) return [];
  const sorted = [...items].sort((a, b) => b.y - a.y || a.x - b.x);
  const rows: PdfTextItem[][] = [];
  let currentY = sorted[0]!.y;
  let parts: PdfTextItem[] = [];
  for (const item of sorted) {
    if (Math.abs(item.y - currentY) > yTolerance) {
      if (parts.length) rows.push(parts);
      parts = [item];
      currentY = item.y;
      continue;
    }
    parts.push(item);
  }
  if (parts.length) rows.push(parts);
  return rows;
}

function rowText(row: PdfTextItem[]): string {
  return row
    .map((item) => item.str)
    .join(' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function parseMoney(raw: string): number | null {
  const cleaned = raw.replace(/[$,\s]/g, '').trim();
  if (!cleaned) return null;
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : null;
}

function samePrice(a: number | null, b: number | null): boolean {
  if (a == null || b == null) return false;
  return Math.abs(a - b) < 0.0005;
}

/** Pull amount tokens from Current / Market / Propose / Approved x-bands. */
function pricesFromRow(row: PdfTextItem[]): {
  current: number | null;
  market: number | null;
  propose: number | null;
  approved: number | null;
} {
  const amounts: { x: number; value: number }[] = [];
  const ordered = [...row].sort((a, b) => a.x - b.x);
  for (let i = 0; i < ordered.length; i += 1) {
    const token = ordered[i]!.str.trim();
    if (token === '$') {
      const next = ordered[i + 1]?.str ?? '';
      const value = parseMoney(next);
      if (value != null) {
        amounts.push({ x: ordered[i]!.x, value });
        i += 1;
      }
      continue;
    }
    if (/^\$?\d/.test(token) && token.includes('.')) {
      const value = parseMoney(token);
      if (value != null) amounts.push({ x: ordered[i]!.x, value });
    }
  }

  let current: number | null = null;
  let market: number | null = null;
  let propose: number | null = null;
  let approved: number | null = null;
  for (const amt of amounts) {
    if (amt.x < 195) current = amt.value;
    else if (amt.x < 250) market = amt.value;
    else if (amt.x < 290) propose = amt.value;
    else if (amt.x < 345) approved = amt.value;
  }
  return { current, market, propose, approved };
}

function itemNoFromRow(row: PdfTextItem[]): string {
  const candidates = row
    .filter((item) => item.x >= 95 && item.x < 150)
    .map((item) => item.str.trim())
    .filter(Boolean);
  // Prefer SKU-like tokens (letters + digits / dashes), skip pure S/N digits.
  for (const token of candidates) {
    if (/^\d+$/.test(token)) continue;
    if (/^[A-Za-z0-9][A-Za-z0-9._/-]*$/.test(token) && /[A-Za-z]/.test(token)) {
      return token.toUpperCase();
    }
  }
  return candidates.find((token) => !/^\d+$/.test(token))?.toUpperCase() ?? '';
}

function snFromRow(row: PdfTextItem[]): string {
  const left = row
    .filter((item) => item.x < 95)
    .map((item) => item.str.trim())
    .find((token) => /^\d+$/.test(token));
  return left ?? '';
}

function uomFromRow(row: PdfTextItem[]): string {
  const token = row
    .filter((item) => item.x >= 340 && item.x < 370)
    .map((item) => item.str.trim())
    .find((value) => /^[A-Za-z]{2,}$/.test(value));
  return token ? token.toUpperCase() : '';
}

/** MOQ column only — blank stays blank. Ignore Remarks. */
function moqFromRow(row: PdfTextItem[]): string {
  const parts = row
    .filter((item) => item.x >= 370 && item.x < 430)
    .map((item) => item.str.trim())
    .filter(Boolean);
  if (parts.length === 0) return '';
  const joined = parts.join(' ').replace(/\s+/g, ' ').trim();
  const leading = /^(\d+(?:\.\d+)?)/.exec(joined);
  return leading?.[1] ?? '';
}

function parseCustomer(rows: PdfTextItem[][]): {
  customerNo: string;
  customerName: string;
  customerPriceGroup: string;
  referenceNo: string;
} {
  let customerNo = '';
  let customerName = '';
  let customerPriceGroup = '';
  let referenceNo = '';

  for (const row of rows) {
    const text = rowText(row);
    if (/PRICE\s+REVIEW\s+FORM/i.test(text) === false && /Reference\s*No/i.test(text)) {
      const ref = /Reference\s*No\.?\s*:?\s*([0-9/A-Za-z-]+)/i.exec(text);
      if (ref?.[1]) referenceNo = ref[1].trim();
    }
    if (/Customer\s+No/i.test(text)) {
      // Prefer the value cell after the label (x ~151).
      const valueCell = row
        .filter((item) => item.x >= 140 && item.x < 340)
        .map((item) => item.str)
        .join(' ')
        .replace(/\s+/g, ' ')
        .trim();
      const source = valueCell || text.replace(/^.*?Customer\s+No\.?\s*&?\s*Name\s*:?\s*/i, '');
      const match = /^([A-Za-z0-9]+)\s+(.+)$/.exec(source.trim());
      if (match) {
        customerNo = match[1]!.trim().toUpperCase();
        customerName = match[2]!
          .replace(/\s+Customer\s+Type:.*$/i, '')
          .trim();
      }
    }
    if (/Customer\s+Price\s+Group/i.test(text)) {
      const valueCell = row
        .filter((item) => item.x >= 140 && item.x < 340)
        .map((item) => item.str)
        .join(' ')
        .replace(/\s+/g, ' ')
        .trim();
      customerPriceGroup = (valueCell || text.replace(/^.*?Customer\s+Price\s+Group\s*:?\s*/i, ''))
        .replace(/\s+Payment\s+Terms:.*$/i, '')
        .trim();
    }
  }

  return { customerNo, customerName, customerPriceGroup, referenceNo };
}

function parseItemRows(rows: PdfTextItem[][]): PrfParsedLine[] {
  const out: PrfParsedLine[] = [];
  let inTable = false;
  for (const row of rows) {
    const text = rowText(row);
    if (/Item\s*No\.?/i.test(text) && /Current/i.test(text) && /Approved/i.test(text)) {
      inTable = true;
      continue;
    }
    if (!inTable) continue;
    if (/^Storage\s+Option/i.test(text) || /^Reason\s+to\s+review/i.test(text)) break;
    if (/^Acknowledgement/i.test(text)) break;

    const sn = snFromRow(row);
    const item_no = itemNoFromRow(row);
    if (!item_no) continue;
    // Skip header leftovers / non-line rows.
    if (/^S\/?N$/i.test(item_no) || /^Item$/i.test(item_no)) continue;
    // Require an S/N digit when present on the left; otherwise need at least one price.
    if (sn && !/^\d+$/.test(sn)) continue;
    const prices = pricesFromRow(row);
    if (!sn && prices.current == null && prices.approved == null) continue;

    out.push({
      sn: sn || String(out.length + 1),
      item_no,
      current: prices.current,
      market: prices.market,
      propose: prices.propose,
      approved: prices.approved,
      uom: uomFromRow(row),
      moq: moqFromRow(row),
    });
  }
  return out;
}

function shouldImportLine(line: PrfParsedLine): { ok: boolean; reason?: string } {
  // Blank current → allow. Same current & approved → ignore.
  if (line.current != null && line.approved != null && samePrice(line.current, line.approved)) {
    return { ok: false, reason: 'Current price equals Approved — ignored' };
  }
  if (line.current == null && line.approved == null) {
    return { ok: false, reason: 'No Current / Approved price to compare' };
  }
  return { ok: true };
}

async function enrichCustomer(
  customerNo: string,
  fallbackName: string,
  fallbackPriceGroup: string,
): Promise<{ customer_no: string; customer_name: string; customer_price_group: string }> {
  const code = customerNo.trim().toUpperCase();
  if (!code) {
    return {
      customer_no: '',
      customer_name: fallbackName,
      customer_price_group: fallbackPriceGroup,
    };
  }
  try {
    const rows = await searchPufCustomers(code);
    const exact =
      rows.find((row) => row.no.trim().toUpperCase() === code) ??
      rows.find((row) => row.no.trim().toUpperCase().startsWith(code));
    if (exact) {
      return {
        customer_no: exact.no.trim(),
        customer_name: exact.name.trim() || fallbackName,
        customer_price_group:
          String(exact.customer_price_group ?? '').trim() || fallbackPriceGroup,
      };
    }
  } catch {
    // keep PDF values
  }
  return {
    customer_no: code,
    customer_name: fallbackName,
    customer_price_group: fallbackPriceGroup,
  };
}

async function enrichItem(
  line: PrfParsedLine,
): Promise<PricingUpdateItemRow> {
  let description = '';
  let uom = line.uom;
  try {
    const rows = await searchPufItems(line.item_no);
    const exact =
      rows.find((row) => row.no.trim().toUpperCase() === line.item_no.toUpperCase()) ?? rows[0];
    if (exact) {
      description = String(exact.description ?? '').trim();
      if (!uom) uom = String(exact.sales_uom ?? '').trim();
    }
  } catch {
    // keep PDF values
  }
  return createPricingUpdateItemRow({
    item_no: line.item_no,
    item_description: description,
    sales_uom: uom,
    sales_price: '',
    moq: line.moq,
    effective_date: '',
    ending_date: '',
  });
}

export async function parsePriceReviewPdfFile(file: File): Promise<PrfImportResult> {
  if (!file.name.toLowerCase().endsWith('.pdf') && file.type !== 'application/pdf') {
    throw new Error('Please drop a Price Review Form PDF.');
  }
  const buffer = await file.arrayBuffer();
  const bytes = new Uint8Array(buffer);
  const items = await withTimeout(
    extractPdfTextItems(bytes),
    PDF_TEXT_TIMEOUT_MS,
    'Price Review PDF read',
  );
  const rows = groupRows(items);
  const joined = rows.map(rowText).join('\n');
  if (!/PRICE\s+REVIEW\s+FORM/i.test(joined) && !/PU-003/i.test(joined)) {
    throw new Error('This PDF does not look like a Price Review Form (PU-003).');
  }

  const header = parseCustomer(rows);
  if (!header.customerNo) {
    throw new Error('Could not find Customer No. in the Price Review PDF.');
  }

  const lines = parseItemRows(rows);
  if (lines.length === 0) {
    throw new Error('No item lines found in the Price Review PDF.');
  }

  const skipped: PrfImportSkip[] = [];
  const toImport: PrfParsedLine[] = [];
  for (const line of lines) {
    const decision = shouldImportLine(line);
    if (!decision.ok) {
      skipped.push({ item_no: line.item_no, reason: decision.reason || 'Skipped' });
      continue;
    }
    toImport.push(line);
  }

  if (toImport.length === 0) {
    throw new Error(
      `No lines to import — every item has the same Current and Approved price (${skipped.length} ignored).`,
    );
  }

  const customer = await enrichCustomer(
    header.customerNo,
    header.customerName,
    header.customerPriceGroup,
  );
  const imported: PricingUpdateItemRow[] = [];
  for (const line of toImport) {
    imported.push(await enrichItem(line));
  }

  return {
    referenceNo: header.referenceNo,
    customerNo: customer.customer_no,
    customerName: customer.customer_name,
    customerPriceGroup: customer.customer_price_group,
    imported,
    skipped,
    formPatch: {
      customer_no: customer.customer_no,
      customer_name: customer.customer_name,
      customer_price_group: customer.customer_price_group,
      reason_requestor: '',
      items: imported,
    },
  };
}

export function formatPrfImportSummary(result: PrfImportResult): string {
  const parts = [
    `Imported ${result.imported.length} item${result.imported.length === 1 ? '' : 's'}`,
  ];
  if (result.customerNo) parts.push(`customer ${result.customerNo}`);
  if (result.referenceNo) parts.push(`from ${result.referenceNo}`);
  if (result.skipped.length > 0) {
    parts.push(`${result.skipped.length} ignored (same Current/Approved)`);
  }
  return `${parts.join(' · ')}. Reason is left blank — fill it before submit.`;
}
